import { useContext, useMemo } from 'react'
import { LotContext } from '../../Lot/LotContext'
import type { WemoloMenuOption } from '../../../components/Wemolo'
import { DeviceTypeEnum } from '../../../data/enums'

/**
 * Dropdown options for the device filter: the lot's device_ids.
 *
 * device_id is the number embedded in the station name (`{lot}_{number}_{station_type}`,
 * e.g. `t001_3_pd` → 3), matching the per-station id the backend reports on detection rows.
 * A device linked to several stations reports under several ids (e.g. `t001_3_pd` and
 * `t001_5_pd` → 3 and 5), so we read the id from each station name rather than counting
 * stations (`i + 1` would mis-map ids when they're sparse). Only scanners (PD) produce
 * detections, so barriers/terminals are excluded.
 *
 * The request filters on `device_ids`, so each option's value is the numeric id. The label pairs
 * that id with the station's device_name (e.g. `1 (PD_123)`) so the picker is readable; a device on
 * several stations shows up under each of its ids, each labelled with the same name.
 */
export const useDeviceFilterOptions = (): WemoloMenuOption[] => {
    const { lotStations } = useContext(LotContext)

    return useMemo(() => {
        const scanners = (lotStations?.station ?? []).filter(
            (s) => Boolean(s.device_uuid) && s.type === DeviceTypeEnum.SCANNER
        )
        return scanners
            .map((station) => {
                const deviceId = Number.parseInt(station.station_name.split('_')[1], 10)
                const deviceName = station.device?.device_name
                return { deviceId, deviceName }
            })
            .filter(({ deviceId }) => Number.isInteger(deviceId))
            .sort((a, b) => a.deviceId - b.deviceId)
            .map(({ deviceId, deviceName }) => {
                const id = String(deviceId)
                return { value: id, label: deviceName ? `${id} (${deviceName})` : id }
            })
    }, [lotStations])
}
